import { createClient, RoomEvent, RoomMemberEvent, ClientEvent, HttpApiEvent } from "matrix-js-sdk";
import { heicToJpeg } from "heic-converter";

import { initI18n, t } from "../components/global_chat/i18n";
import { applyIosFixes } from "../components/global_chat/ios_fixes";
import { BryghtChatUI } from "../components/global_chat/ui";
import { waitForRoom } from "../components/global_chat/utils";

applyIosFixes();

// ─── Bryght Chat – powered by matrix-js-sdk (Apache 2.0) ───
// Matrix/Tuwunel is used as a pure messaging transport layer.
// All user display info (names, avatars) comes from Rails.

// The chat that is running: its container, Matrix client and UI, plus a promise
// for "synced and drawn". One at a time. The entrypoint is on every page and
// the drawer is turbo-permanent, so normally the same container keeps turning
// up and there is nothing to do.
let activeChat = null;

function retireActiveChat() {
  if (!activeChat) return;

  const { client, ui } = activeChat;
  activeChat = null;
  ui?.destroy();
  try {
    client?.stopClient();
  } catch (_) { /* already stopped */ }
}

async function initBryghtChat() {
  const container = document.getElementById("bryght-chat");
  if (!container) return;

  // The container this chat already runs in: nothing to do.
  if (activeChat && activeChat.container === container) return;

  // Any other container is a fresh start — even one that looks initialised.
  // Turbo restores pages from cached snapshots, and a snapshot's chat container
  // is a clone: the same markup and data attributes, but no listeners and no
  // client behind it. It lands in the document when the live container had
  // left it — in the iOS app, a back swipe out of the chat that is cancelled
  // halfway: Hotwire Native answers that with a restore visit, and the page
  // came back from the snapshot with a chat nothing could be clicked in. The
  // element used to carry a data-initialized flag that the clone inherited, so
  // the chat was never started again. The client of the element it was copied
  // from is retired here.
  retireActiveChat();

  let markReady;
  const chat = { container, client: null, ui: null, ready: null };
  chat.ready = new Promise((resolve) => { markReady = resolve; });
  activeChat = chat;

  initI18n(container);

  const {
    homeserverUrl,
    accessToken,
    userId,
    deviceId,
    roomMap: roomMapJson,
    userMap: userMapJson,
  } = container.dataset;

  if (!homeserverUrl || !accessToken || !userId) {
    container.innerHTML = '<p class="bryght-chat-error">Chat-Konfiguration fehlt.</p>';
    markReady();
    return;
  }

  // Room map: matrix_room_id → { name, avatar_url, avatar_large_url } of the other party.
  // User map: matrix_user_id → { name, avatar_url, avatar_large_url } of everyone who can
  // appear as a sender. All display info comes from Rails, not from Matrix profiles.
  const roomMap = JSON.parse(roomMapJson || "{}");
  const userMap = JSON.parse(userMapJson || "{}");
  console.log("[BryghtChat] Starting init...", { userId, homeserverUrl, usersLoaded: Object.keys(userMap).length });

  try {
    // 1. Create Matrix client
    const client = createClient({
      baseUrl: homeserverUrl,
      accessToken,
      userId,
      deviceId,
      useAuthorizationHeader: true,
    });
    chat.client = client;

    // 1b. Recover from an access token the homeserver no longer accepts
    // Tuwunel answers M_UNKNOWN_TOKEN once the session behind this token ended
    // (signed out in another tab, expired). The SDK then stops syncing, which
    // looks exactly like "messages stop arriving" — so reload the chat frame and
    // let Rails mint a fresh token instead of dying silently.
    let sessionRecoveryStarted = false;

    function recoverExpiredSession(reason) {
      if (sessionRecoveryStarted) return;
      sessionRecoveryStarted = true;

      console.warn("[BryghtChat] Matrix session no longer valid:", reason);

      try {
        client.stopClient();
      } catch (_) { /* the SDK already stopped itself on M_UNKNOWN_TOKEN */ }

      reloadChatFrame(container);
    }

    // Fires for *any* authed request the homeserver rejects with M_UNKNOWN_TOKEN,
    // the sync loop included.
    client.on(HttpApiEvent.SessionLoggedOut, (err) => {
      recoverExpiredSession(err?.errcode || "SessionLoggedOut");
    });

    // The sync loop also surfaces it as a terminal sync error — catch it here too,
    // because a token rejected on the very first sync never reaches the listener
    // above in a state where the UI is up.
    client.on(ClientEvent.Sync, (state, prevState, data) => {
      if (state === "ERROR" && data?.error?.errcode === "M_UNKNOWN_TOKEN") {
        recoverExpiredSession("sync M_UNKNOWN_TOKEN");
      }
    });

    // 2. Initialize UI class
    const ui = new BryghtChatUI(client, userId, userMap, t, roomMap);
    chat.ui = ui;

    // 3. Real-time event listeners

    // The element already showing this message, if any: by event id, or —
    // for the server's copy of a message this client sent — by the transaction
    // id its local echo was rendered with. Normally the SDK folds that copy
    // into the echo itself; after a timeline reset (the join of a room Rails
    // just provisioned) it arrives as a fresh event instead, and rendering it
    // would show the message twice. Adopting the id keeps reactions, replies
    // and redactions on the one element that stays.
    const renderedMessageFor = (timeline, event) => {
      const byId = timeline.querySelector(`.bryght-message[data-event-id="${CSS.escape(event.getId())}"]`);
      if (byId) return byId;

      const txnId = event.getUnsigned()?.transaction_id;
      if (!txnId) return null;
      const byTxn = timeline.querySelector(`.bryght-message[data-txn-id="${CSS.escape(txnId)}"]`);
      if (byTxn) adoptEventId(byTxn, event);
      return byTxn;
    };

    const adoptEventId = (msgEl, event) => {
      msgEl.dataset.eventId = event.getId();
      msgEl._matrixEvent = event;
      const reactionsEl = msgEl.querySelector(".bryght-reactions");
      if (reactionsEl) reactionsEl.dataset.eventId = event.getId();
    };

    // The local echo got its server id: the element rendered under the
    // temporary one has to answer to the real one from now on.
    client.on(RoomEvent.LocalEchoUpdated, (event, room, oldEventId) => {
      if (room.roomId !== ui.currentRoomId || !oldEventId || oldEventId === event.getId()) return;
      const msgEl = document.querySelector(`.bryght-message[data-event-id="${CSS.escape(oldEventId)}"]`);
      if (msgEl) adoptEventId(msgEl, event);
    });

    client.on(RoomEvent.Timeline, (event, room, toStartOfTimeline) => {
      const eventType = event.getType();
      // RoomEvent.Redaction only fires for targets loaded in the SDK timeline;
      // gallery-only history also needs the raw redaction arriving through sync.
      if (eventType === "m.room.redaction") ui.handleMediaRedaction(event, room);

      if (toStartOfTimeline) {
        if (eventType !== "m.room.message") return;
        // A jump to an old message loads many pages at once and renders them
        // in one pass afterwards (ui.renderMissingOlderEvents).
        if (ui.isBackfillRenderingSuspended(room.roomId)) return;
        if (room.roomId === ui.currentRoomId) {
          const timeline = document.getElementById("bryght-timeline");
          if (timeline && !renderedMessageFor(timeline, event)) {
            const msgEl = ui.renderTimelineEvent(event);
            if (msgEl) {
              const spinner = timeline.querySelector(".bryght-pagination-spinner");
              if (spinner && spinner.nextSibling) {
                timeline.insertBefore(msgEl, spinner.nextSibling);
              } else {
                timeline.prepend(msgEl);
              }
              ui.ensureDateDividers(timeline);
            }
          }
        }
        return;
      }

      if (eventType === "m.reaction") {
        if (room.roomId === ui.currentRoomId) {
          import("../components/global_chat/reactions").then(mod => {
             mod.renderReactionOnMessage(event, client);
          });
        }
        return;
      }

      if (eventType !== "m.room.message") return;

      if (room.roomId === ui.currentRoomId) {
        const timeline = document.getElementById("bryght-timeline");
        if (timeline && !renderedMessageFor(timeline, event)) {
          const msgEl = ui.renderTimelineEvent(event);
          if (msgEl) {
            msgEl.classList.add("bryght-message--new");
            msgEl.addEventListener("animationend", () => {
              msgEl.classList.remove("bryght-message--new");
            }, { once: true });
            
            const isNearBottom = timeline.dataset.atBottom === "true" || (timeline.scrollHeight - timeline.scrollTop - timeline.clientHeight < 150);
            const isOwnMessage = event.getSender() === userId;
            
            timeline.appendChild(msgEl);
            ui.ensureDateDividers(timeline);
            
            if (isNearBottom || isOwnMessage) {
              requestAnimationFrame(() => {
                timeline.scrollTop = timeline.scrollHeight;
                timeline.dataset.atBottom = "true";
              });
            } else {
              // Scrolled up in the history – the message landed out of sight.
              ui.bumpNewMessagesIndicator();
            }
          }
        }
        import("../components/global_chat/reactions").then(mod => {
           mod.sendReadReceiptForRoom(client, room);
        });
        // The Rails-fed unread badge needs the same news (BRY-928); the receipt
        // above only travels between Matrix clients.
        ui.scheduleMarkRoomRead(room);
      }

      ui.renderRoomList();
    });

    client.on(RoomMemberEvent.Typing, (event, member) => {
      if (member.roomId !== ui.currentRoomId) return;
      if (member.userId === userId) return;

      const typingEl = document.querySelector(".bryght-typing-indicator");
      if (!typingEl) return;

      const room = client.getRoom(ui.currentRoomId);
      if (!room) return;

      const typingMembers = room.getMembers().filter((m) => m.typing && m.userId !== userId);

      if (typingMembers.length === 0) {
        typingEl.style.display = "none";
        return;
      }

      const names = typingMembers.map((m) => ui.getUserInfo(m.userId).name);
      const text = names.length === 1
        ? t("typing_one", `${names[0]} tippt…`).replace("%{name}", names[0])
        : t("typing_other", `${names.join(", ")} tippen…`).replace("%{names}", names.join(", "));

      typingEl.textContent = text;
      typingEl.style.display = "block";
    });

    client.on(RoomEvent.Redaction, (event, room) => {
      // Gallery caches outlive the open conversation, so background rooms
      // must receive redactions too.
      ui.handleMediaRedaction(event, room);
      if (room.roomId !== ui.currentRoomId) return;

      // ── Replace redacted message with "deleted" placeholder ──
      const redactedEventId = event.event?.redacts;
      if (redactedEventId) {
        const msgEl = document.querySelector(`.bryght-message[data-event-id="${redactedEventId}"]`);
        if (msgEl) {
          // Find the original event in the timeline to get sender/timestamp
          const origEvent = room.getLiveTimeline().getEvents().find(e => e.getId() === redactedEventId);
          if (origEvent) {
            const placeholder = ui.renderRedactedPlaceholder(origEvent);
            if (placeholder) {
              msgEl.replaceWith(placeholder);
              // The fresh element carries no grouping classes yet.
              ui.applyMessageGrouping(document.getElementById("bryght-timeline"));
            }
          }
        }
      }

      // ── Re-render reaction badges (existing logic) ──
      document.querySelectorAll(".bryght-reactions").forEach((el) => {
        const targetId = el.dataset.eventId;
        el.innerHTML = "";
        const myUserId = client.getUserId();
        const reactions = new Map();
        const timelineEvents = room.getLiveTimeline().getEvents();
        for (const ev of timelineEvents) {
          if (ev.getType() !== "m.reaction") continue;
          if (ev.isRedacted()) continue;
          const r = ev.getContent()["m.relates_to"];
          if (r?.event_id === targetId && r?.key) {
            const existing = reactions.get(r.key) || { count: 0, isMine: false };
            existing.count++;
            if (ev.getSender() === myUserId) existing.isMine = true;
            reactions.set(r.key, existing);
          }
        }
        reactions.forEach(({ count, isMine }, emoji) => {
          const badge = document.createElement("span");
          badge.className = "bryght-reaction-badge";
          if (isMine) badge.classList.add("bryght-reaction-mine");
          badge.textContent = `${emoji} ${count}`;
          el.appendChild(badge);
        });
      });
    });

    client.on(RoomEvent.MyMembership, () => ui.renderRoomList());
    client.on(RoomEvent.Name, () => ui.renderRoomList());
    client.on(RoomMemberEvent.Membership, () => ui.renderRoomList());

    // 4. Start sync
    console.log("[BryghtChat] Starting sync...");
    await client.startClient({ initialSyncLimit: 30 });

    await new Promise((resolve) => {
      const onSync = (state) => {
        if (state === "PREPARED" || state === "SYNCING") {
          client.removeListener(ClientEvent.Sync, onSync);
          resolve();
        }
      };
      client.on(ClientEvent.Sync, onSync);
    });

    console.log("[BryghtChat] Sync ready! Building UI...");
    container.classList.add("bryght-chat-ready");

    // 5. Initial render
    ui.renderRoomList();

    let targetRoomId = sessionStorage.getItem("bryght_active_room");
    const rooms = client.getRooms().filter((r) => r.getMyMembership() === "join");
    rooms.sort((a, b) => (b.getLastActiveTimestamp() || 0) - (a.getLastActiveTimestamp() || 0));

    const targetRoom = targetRoomId ? rooms.find((r) => r.roomId === targetRoomId) : null;

    if (rooms.length > 0) {
      if (targetRoom) {
        ui.selectRoom(targetRoom);
      } else if (ui.rightPanel) {
        ui.rightPanel.innerHTML = `<div class="bryght-empty-state"><p>${t("select_chat_title", "Wähle einen Chat")}</p><p>${t("select_chat_text", "Wähle einen Chat aus der Liste aus.")}</p></div>`;
      }
    } else if (ui.rightPanel) {
      ui.rightPanel.innerHTML = `<div class="bryght-empty-state"><p>${t("empty_state_title", "Noch keine Chats vorhanden.")}</p><p>${t("empty_state_text", "Starte eine neue Nachricht über den Button links oben.")}</p></div>`;
    }

    console.log("[BryghtChat] Chat ready!", rooms.length, "rooms");
    markReady();
  } catch (err) {
    console.error("[BryghtChat] Init failed:", err);
    container.innerHTML = `<p class="bryght-chat-error">Der Chat konnte nicht geladen werden: ${err.message}</p>`;
    markReady();
  }
}

// Open a room Rails just provisioned. The dialog announces it through the
// open_chat_room Turbo Stream action (helpers/turbo_stream_actions.js), which
// carries the display info Rails owns — Matrix profiles are never the source
// for names or avatars here.
// roomInfo arrives in the shape MatrixChat::DisplayPayload produces, the same
// one the bootstrapped room map holds, id included: without it the SPA has no
// Rails id for a room created after the page was rendered, and the action
// endpoints that take one — mute, unmute, leave, dissolve — silently do
// nothing. mark_read is not among them; it names the room by its Matrix id.
//
// Registered once, at module level, and routed to whichever chat is running:
// a listener per init would outlive the chat it was made for.
window.addEventListener("bryght-chat:open-room", async (event) => {
  const { roomId, roomInfo, matrixUserId } = event.detail || {};
  if (!roomId || !roomInfo) return;

  const chat = activeChat;
  if (!chat) return;
  await chat.ready;
  if (activeChat !== chat || !chat.ui) return;
  const { client, ui } = chat;

  ui.roomMap[roomId] = roomInfo;

  // A DM payload is the peer's identity, keyed by room. The SPA also needs it
  // keyed by sender, or the first message renders under a raw MXID.
  if (matrixUserId) {
    ui.userMap[matrixUserId] = {
      name: roomInfo.name,
      avatar_url: roomInfo.avatar_url,
      avatar_large_url: roomInfo.avatar_large_url
    };
  }

  const room = await waitForRoom(client, roomId);
  if (!room || activeChat !== chat) return;

  ui.renderRoomList();
  ui.selectRoom(room);
});

// Open a draft: the picker chose someone the user shares no room with yet.
// Rails created nothing — the SPA shows the peer and a composer, and the first
// message sent there is what creates the room (ui.provisionDraftRoom). Same
// payload shape as above, minus the room.
window.addEventListener("bryght-chat:open-draft", async (event) => {
  const chat = activeChat;
  if (!chat) return;
  await chat.ready;
  if (activeChat !== chat || !chat.ui) return;

  chat.ui.openDraft(event.detail || {});
});

// ── Chat frame reload ────────────────────────────────────────
// Re-fetches /chat into the lazily-loaded turbo-frame. Rails runs the JWT login
// again and renders a fresh access token into the container; the global
// turbo:frame-load listener below re-runs initBryghtChat.
//
// Rate-limited: if the freshly minted token is rejected too, the sequence
// "reload → M_UNKNOWN_TOKEN → reload" would spin forever, so a second attempt
// within the cooldown shows an error instead of reloading again.
const CHAT_RELOAD_COOLDOWN_MS = 30000;

function reloadChatFrame(container) {
  const lastReload = Number(sessionStorage.getItem("bryght_chat_reloaded_at") || 0);

  if (Date.now() - lastReload < CHAT_RELOAD_COOLDOWN_MS) {
    container.innerHTML =
      `<p class="bryght-chat-error">${t("session_expired", "Deine Chat-Sitzung ist abgelaufen. Bitte lade die Seite neu.")}</p>`;
    return;
  }

  sessionStorage.setItem("bryght_chat_reloaded_at", String(Date.now()));

  const frame = document.getElementById("global-chat-frame");
  if (!frame) {
    window.location.reload();
    return;
  }

  // The drawer strips `src` once the frame has loaded (see chat_drawer_controller)
  // and restores that state here, so navigating on doesn't re-fetch the frame.
  frame.addEventListener("turbo:frame-load", () => frame.removeAttribute("src"), { once: true });
  frame.removeAttribute("src");
  frame.src = "/chat";
}

document.addEventListener("turbo:load", initBryghtChat);
document.addEventListener("turbo:frame-load", (e) => {
  if (e.target.id === "global-chat-frame") initBryghtChat();
});
if (document.getElementById("bryght-chat")) initBryghtChat();

document.addEventListener("turbo:before-render", (event) => {
  const newBody = event.detail.newBody;
  if (document.body.classList.contains("chat-drawer-open")) newBody.classList.add("chat-drawer-open");
  if (document.body.classList.contains("chat-drawer-fullscreen")) newBody.classList.add("chat-drawer-fullscreen");
  if (document.body.classList.contains("no-chat-transition")) newBody.classList.add("no-chat-transition");
});

document.addEventListener("turbo:before-render", () => {
  const scroller = document.querySelector("#bryght-chat .os-viewport") || document.querySelector("#bryght-chat .MatrixChat");
  if (scroller) scroller.dataset.savedScrollTop = scroller.scrollTop;
});
document.addEventListener("turbo:render", () => {
  const scroller = document.querySelector("#bryght-chat .os-viewport") || document.querySelector("#bryght-chat .MatrixChat");
  if (scroller && scroller.dataset.savedScrollTop) {
    scroller.scrollTop = scroller.dataset.savedScrollTop;
  }
});
